import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ClientCombobox from "@/components/ClientCombobox";
import { FileText, Plus, Trash2, Eye, Wallet, XCircle } from "lucide-react";
import { canAccessModule } from "@/utils/permissions";
import "./cabina.css";

const fmt = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

const chipEstatus = (e) => {
  const map = {
    pendiente: ["c-pend", "Pendiente"],
    parcial: ["c-warn", "Parcial"],
    pagada: ["c-fact", "Pagada"],
  };
  const [cls, l] = map[e] || ["c-pend", e || "—"];
  return <span className={`chip ${cls}`}>{l}</span>;
};

export default function AdminFacturacionGlobal() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(null);
  const [showNueva, setShowNueva] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const isAdmin = canAccessModule(user, "admin_facturacion_global");

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [inv, cc] = await Promise.all([
        axios.get(`${API}/facturacion-global/invoices`),
        cat ? Promise.resolve({ data: cat }) : axios.get(`${API}/cabina/catalogo`),
      ]);
      setInvoices(inv.data.invoices || []);
      if (!cat) setCat(cc.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar");
    }
    setLoading(false);
  }, [isAdmin, cat]);

  useEffect(() => { load(); }, [load]);

  const eliminar = async (gi) => {
    const motivo = window.prompt(`¿Motivo para borrar la factura global ${gi.folio || gi.id.slice(0, 8)} (mínimo 3 caracteres)?`, "");
    if (!motivo || motivo.trim().length < 3) return;
    if (!window.confirm(`Se borrará la factura global y todas sus sombras en cada expediente involucrado. ¿Continuar?`)) return;
    try {
      await axios.delete(`${API}/facturacion-global/invoices/${gi.id}`, { params: { motivo } });
      toast.success("Factura global borrada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!isAdmin) {
    return (
      <div className="cv" style={{ padding: 22 }}>
        <div className="rulebox" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
          <b>Sin acceso.</b> No tienes permiso para gestionar facturas globales.
        </div>
      </div>
    );
  }

  return (
    <div className="cv" style={{ padding: 22 }} data-testid="admin-facturacion-global">
      <div className="head">
        <div>
          <h2>Facturación Global</h2>
          <p>Agrupa varios expedientes de un mismo cliente en una sola factura CFDI. En cada expediente se refleja la porción parcial correspondiente.</p>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn dark sm" onClick={() => setShowNueva(true)} data-testid="btn-nueva-fg">
            <Plus size={13} /> Nueva factura global
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="tw">
          {loading ? (
            <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Cargando...</p>
          ) : invoices.length === 0 ? (
            <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Sin facturas globales registradas.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th><th>Fecha</th><th>Cliente</th>
                  <th>Método</th><th>Expedientes</th>
                  <th className="r">Subtotal</th><th className="r">IVA</th><th className="r">Total</th>
                  <th className="r">Cobrado</th><th className="r">Saldo</th>
                  <th>Estatus</th><th className="r" style={{ width: 130 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((gi) => (
                  <tr key={gi.id} data-testid={`row-fg-${gi.id}`}>
                    <td><b>{gi.folio || <span style={{ color: "var(--cv-muted)" }}>s/f</span>}</b></td>
                    <td className="mono">{fmtDate(gi.fecha)}</td>
                    <td>{gi.client_name || "—"}</td>
                    <td><span className="chip c-val">{gi.metodo}</span></td>
                    <td style={{ textAlign: "center" }}>{(gi.expedientes || []).length}</td>
                    <td className="r mono">{fmt(gi.subtotal)}</td>
                    <td className="r mono">{fmt(gi.iva)}</td>
                    <td className="r mono"><b>{fmt(gi.total)}</b></td>
                    <td className="r mono">{fmt(gi.cobrado)}</td>
                    <td className="r mono">{fmt(gi.saldo)}</td>
                    <td>{chipEstatus(gi.estatus_pago)}</td>
                    <td className="r">
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button className="btn ghost sm" onClick={() => setDetalle(gi)} title="Ver detalle" data-testid={`btn-ver-fg-${gi.id}`}>
                          <Eye size={12} />
                        </button>
                        <button className="btn ghost sm" onClick={() => eliminar(gi)} title="Borrar" style={{ color: "#991b1b", borderColor: "#fca5a5" }} data-testid={`btn-borrar-fg-${gi.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNueva && (
        <NuevaFacturaGlobalDialog cat={cat} onClose={() => setShowNueva(false)} onSaved={() => { setShowNueva(false); load(); }} />
      )}
      {detalle && (
        <DetalleGlobalDialog invoice={detalle} onClose={() => setDetalle(null)} onChanged={() => { setDetalle(null); load(); }} />
      )}
    </div>
  );
}

// -------- Nueva Factura Global --------
function NuevaFacturaGlobalDialog({ cat, onClose, onSaved }) {
  const [cliente, setCliente] = useState(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [candidatos, setCandidatos] = useState([]);
  const [sel, setSel] = useState({}); // {expediente_id: {selected, subtotal, iva, override}}
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({
    folio: "", uuid: "", fecha: new Date().toISOString().slice(0, 10),
    estatus_pago: "pendiente", forma: "99", fecha_pago: "",
    dias_credito: 0,
  });

  const esPPD = meta.estatus_pago === "pendiente";
  const formasList = (cat?.formas_pago_sat || []).filter((f) => (esPPD ? (f.clave || f.codigo) === "99" : (f.clave || f.codigo) !== "99"));

  const buscar = useCallback(async () => {
    if (!cliente?.id) { toast.error("Selecciona un cliente"); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/facturacion-global/expedientes-facturables`, {
        params: { client_id: cliente.id, desde, hasta },
      });
      const exps = r.data.expedientes || [];
      setCandidatos(exps);
      // Precargar selección
      const s = {};
      exps.forEach((e) => {
        s[e.expediente_id] = {
          selected: false,
          subtotal: e.subtotal_sugerido,
          iva: e.iva_sugerido,
          override: false,
        };
      });
      setSel(s);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setLoading(false);
  }, [cliente, desde, hasta]);

  useEffect(() => {
    if (cliente?.id) buscar();
  }, [cliente?.id, buscar]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: { ...s[id], selected: !s[id]?.selected } }));
  const updateMonto = (id, key, val) => setSel((s) => ({
    ...s,
    [id]: { ...s[id], [key]: Number(val || 0), override: true },
  }));

  const totales = useMemo(() => {
    let sub = 0, iva = 0;
    candidatos.forEach((e) => {
      const row = sel[e.expediente_id];
      if (row?.selected) {
        sub += Number(row.subtotal || 0);
        iva += Number(row.iva || 0);
      }
    });
    return { subtotal: sub, iva, total: sub + iva };
  }, [sel, candidatos]);

  const submit = async () => {
    if (!cliente?.id) { toast.error("Selecciona un cliente"); return; }
    const seleccionados = candidatos
      .filter((e) => sel[e.expediente_id]?.selected)
      .map((e) => ({
        expediente_id: e.expediente_id,
        subtotal_parcial: Number(sel[e.expediente_id].subtotal || 0),
        iva_parcial: Number(sel[e.expediente_id].iva || 0),
        manual_override: !!sel[e.expediente_id].override,
      }));
    if (seleccionados.length === 0) { toast.error("Selecciona al menos un expediente"); return; }
    if (meta.estatus_pago === "pagada" && !meta.forma) { toast.error("Elige forma de pago SAT"); return; }
    if (meta.estatus_pago === "pagada" && !meta.fecha_pago) { toast.error("Fecha de pago obligatoria en PUE"); return; }
    try {
      const body = {
        client_id: cliente.id,
        folio: meta.folio || "",
        uuid_cfdi: meta.uuid || "",
        fecha: meta.fecha,
        estatus_pago: meta.estatus_pago,
        forma: meta.forma,
        fecha_pago: meta.fecha_pago,
        dias_credito: Number(meta.dias_credito || 0),
        moneda: "MXN",
        expedientes: seleccionados,
      };
      await axios.post(`${API}/facturacion-global/invoices`, body);
      toast.success("Factura global creada");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 1100, padding: 0, maxHeight: "94vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}><FileText size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Nueva factura global</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>Elige el cliente, filtra por fecha y selecciona los expedientes a agrupar. Puedes ajustar el monto parcial de cada uno.</p>
        </div>
        <div style={{ padding: 20 }}>
          {/* Filtros */}
          <div className="grid" style={{ gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, marginBottom: 14 }}>
            <label className="f">
              <span>Cliente</span>
              <ClientCombobox value={cliente?.company_name || ""}
                onSelect={(c) => setCliente(c)}
                onClear={() => { setCliente(null); setCandidatos([]); }}
                testid="fg-cliente" />
            </label>
            <label className="f"><span>Desde (fecha venta)</span><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="f"><span>Hasta</span><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button className="btn sm" onClick={buscar} disabled={!cliente} data-testid="btn-fg-buscar">Buscar</button>
            </div>
          </div>

          {/* Tabla de candidatos */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="tw">
              {loading ? <p style={{ padding: 16, color: "var(--cv-muted)", textAlign: "center" }}>Buscando…</p>
                : candidatos.length === 0 ? <p style={{ padding: 16, color: "var(--cv-muted)", textAlign: "center" }}>
                  {cliente ? "Sin expedientes por facturar para este cliente." : "Elige un cliente para ver sus expedientes por facturar."}
                </p>
                : (
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 34 }}></th>
                        <th>Folio</th><th>Fecha</th>
                        <th className="r">Venta</th><th className="r">Facturado</th><th className="r">Por facturar</th>
                        <th className="r">Subtotal parcial</th><th className="r">IVA parcial</th><th className="r">Total parcial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidatos.map((e) => {
                        const row = sel[e.expediente_id] || {};
                        const tot = Number(row.subtotal || 0) + Number(row.iva || 0);
                        return (
                          <tr key={e.expediente_id} data-testid={`fg-row-${e.expediente_id}`} style={row.selected ? { background: "#f7f9ff" } : undefined}>
                            <td><input type="checkbox" checked={!!row.selected} onChange={() => toggle(e.expediente_id)} data-testid={`fg-check-${e.expediente_id}`} /></td>
                            <td className="mono"><b>{e.folio}</b></td>
                            <td className="mono">{fmtDate(e.fecha_venta)}</td>
                            <td className="r mono">{fmt(e.venta_bruta)}</td>
                            <td className="r mono">{fmt(e.facturado_previo)}</td>
                            <td className="r mono"><b>{fmt(e.por_facturar)}</b></td>
                            <td className="r"><input type="number" value={row.subtotal ?? 0} disabled={!row.selected}
                              onChange={(ev) => updateMonto(e.expediente_id, "subtotal", ev.target.value)}
                              style={{ width: 100, textAlign: "right" }} data-testid={`fg-sub-${e.expediente_id}`} /></td>
                            <td className="r"><input type="number" value={row.iva ?? 0} disabled={!row.selected}
                              onChange={(ev) => updateMonto(e.expediente_id, "iva", ev.target.value)}
                              style={{ width: 100, textAlign: "right" }} data-testid={`fg-iva-${e.expediente_id}`} /></td>
                            <td className="r mono"><b>{fmt(tot)}</b></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            </div>
          </div>

          {/* Totales seleccionados */}
          <div style={{ padding: 12, background: "#f7f9ff", borderRadius: 8, marginBottom: 14, fontSize: 13, display: "flex", gap: 24, justifyContent: "flex-end" }}>
            <div>Subtotal: <b className="mono">{fmt(totales.subtotal)}</b></div>
            <div>IVA: <b className="mono">{fmt(totales.iva)}</b></div>
            <div>Total factura: <b className="mono">{fmt(totales.total)}</b></div>
          </div>

          {/* Metadatos de la factura */}
          <div className="grid g2" style={{ gap: 10 }}>
            <label className="f"><span>Folio factura ERP</span><input value={meta.folio} onChange={(e) => setMeta({ ...meta, folio: e.target.value })} placeholder="Ej. FG-2026-001" data-testid="fg-folio" /></label>
            <label className="f"><span>Fecha</span><input type="date" value={meta.fecha} onChange={(e) => setMeta({ ...meta, fecha: e.target.value })} /></label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>UUID (opcional)</span><input value={meta.uuid} onChange={(e) => setMeta({ ...meta, uuid: e.target.value.trim() })} placeholder="00000000-0000-0000-0000-000000000000" data-testid="fg-uuid" /></label>
            <label className="f"><span>Estatus de pago</span>
              <select value={meta.estatus_pago} onChange={(e) => setMeta({ ...meta, estatus_pago: e.target.value, forma: e.target.value === "pagada" ? "" : "99" })} data-testid="fg-estatus">
                <option value="pendiente">Pendiente (PPD)</option>
                <option value="pagada">Pagada (PUE)</option>
              </select>
            </label>
            <label className="f"><span>Forma de pago SAT</span>
              {esPPD ? <input value="99 Por definir" disabled /> : (
                <select value={meta.forma} onChange={(e) => setMeta({ ...meta, forma: e.target.value })} data-testid="fg-forma">
                  <option value="">Elige</option>
                  {formasList.map((f) => <option key={f.clave || f.codigo} value={f.clave || f.codigo}>{f.clave || f.codigo} {f.nombre}</option>)}
                </select>
              )}
            </label>
            <label className="f"><span>Días de crédito</span><input type="number" value={meta.dias_credito} onChange={(e) => setMeta({ ...meta, dias_credito: Number(e.target.value || 0) })} /></label>
            {meta.estatus_pago === "pagada" && <label className="f"><span>Fecha del pago</span><input type="date" value={meta.fecha_pago} onChange={(e) => setMeta({ ...meta, fecha_pago: e.target.value })} /></label>}
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="fg-guardar">Crear factura global</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Detalle Factura Global --------
function DetalleGlobalDialog({ invoice, onClose, onChanged }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/facturacion-global/invoices/${invoice.id}`);
        setFull(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    })();
  }, [invoice.id]);
  if (!full) return null;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 900, padding: 0, maxHeight: "92vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Factura global {full.folio || full.id.slice(0, 8)}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>{full.client_name} · {full.metodo} · {chipEstatus(full.estatus_pago)}</p>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ padding: 12, background: "#f7f9ff", borderRadius: 8, marginBottom: 14, fontSize: 13, display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>Fecha: <b>{fmtDate(full.fecha)}</b></div>
            <div>Subtotal: <b className="mono">{fmt(full.subtotal)}</b></div>
            <div>IVA: <b className="mono">{fmt(full.iva)}</b></div>
            <div>Total: <b className="mono">{fmt(full.total)}</b></div>
            <div>Cobrado: <b className="mono">{fmt(full.cobrado)}</b></div>
            <div>Saldo: <b className="mono">{fmt(full.saldo)}</b></div>
            {full.dias_credito > 0 && <div>Vence: <b>{full.fecha_vencimiento}</b></div>}
          </div>

          <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>Expedientes incluidos</h4>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="tw">
              <table>
                <thead><tr><th>Folio</th><th className="r">Subtotal</th><th className="r">IVA</th><th className="r">Total</th><th style={{ textAlign: "center" }}>Manual</th></tr></thead>
                <tbody>
                  {(full.expedientes || []).map((r) => (
                    <tr key={r.expediente_id}>
                      <td><b>{r.folio_expediente || r.expediente_id.slice(0, 8)}</b></td>
                      <td className="r mono">{fmt(r.subtotal_parcial)}</td>
                      <td className="r mono">{fmt(r.iva_parcial)}</td>
                      <td className="r mono"><b>{fmt(r.total_parcial)}</b></td>
                      <td style={{ textAlign: "center" }}>{r.manual_override ? "Sí" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>Pagos globales aplicados</h4>
          <div className="card">
            <div className="tw">
              {(full.pagos_globales || []).length === 0 ? (
                <p style={{ padding: 14, color: "var(--cv-muted)", textAlign: "center", fontSize: 13 }}>Sin pagos aplicados.</p>
              ) : (
                <table>
                  <thead><tr><th>Fecha</th><th>Forma</th><th>Referencia</th><th className="r">Importe</th></tr></thead>
                  <tbody>
                    {(full.pagos_globales || []).map((p) => (
                      <tr key={p.id}>
                        <td className="mono">{fmtDate(p.fecha)}</td>
                        <td>{p.forma}</td>
                        <td>{p.referencia || "—"}</td>
                        <td className="r mono"><b>{fmt(p.importe_total)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cerrar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
